import { zodResolver } from '@hookform/resolvers/zod';
import {
  updateQueueSettingsSchema,
  type QueueSettings,
  type UpdateQueueSettings,
  type UpdateQueueSettingsInput,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateQueueSettings } from '../../../api/queue-settings';
import { EmailTemplatesSection } from './EmailTemplatesSection';
import { QueueRulesSection } from './QueueRulesSection';
import { SenderSection } from './SenderSection';

export interface QueueSettingsFormProps {
  settings: QueueSettings;
}

/** Campi modificabili del record, nell'ordine dello schema condiviso. */
function toFormValues(settings: QueueSettings): UpdateQueueSettingsInput {
  return {
    senderName: settings.senderName,
    senderLocalPart: settings.senderLocalPart,
    assignmentSubject: settings.assignmentSubject,
    assignmentBody: settings.assignmentBody,
    closureSubject: settings.closureSubject,
    closureBody: settings.closureBody,
    requirePriorityBeforeReply: settings.requirePriorityBeforeReply,
    requireDescriptionOnClose: settings.requireDescriptionOnClose,
    daysResolvedToClosed: settings.daysResolvedToClosed,
  };
}

/** Campi non validi segnalati dall'API (`details.fields` di `validation_error`). */
function invalidFields(details: Record<string, unknown> | undefined): { path: string; message: string }[] {
  const fields = details?.fields;
  if (!Array.isArray(fields)) return [];
  return fields.filter(
    (field): field is { path: string; message: string } =>
      typeof field === 'object' &&
      field !== null &&
      typeof (field as { path?: unknown }).path === 'string' &&
      typeof (field as { message?: unknown }).message === 'string'
  );
}

const FIELD_NAMES = new Set(Object.keys(updateQueueSettingsSchema.shape));

/**
 * Un unico form per i tre blocchi (mittente, testi, regole), con lo stesso schema zod dell'API.
 * «Salva» sostituisce il record intero (ultimo-vince, senza controllo di concorrenza: vedi
 * PAGE-004); la risposta diventa il nuovo stato del form. «Annulla modifiche» torna ai valori
 * salvati.
 */
export function QueueSettingsForm({ settings }: QueueSettingsFormProps) {
  const values = useMemo(() => toFormValues(settings), [settings]);
  const { control, handleSubmit, setError, reset, formState } = useForm<
    UpdateQueueSettingsInput,
    unknown,
    UpdateQueueSettings
  >({
    resolver: zodResolver(updateQueueSettingsSchema),
    values,
  });
  const update = useUpdateQueueSettings();
  const [saved, setSaved] = useState(false);

  const save = (parsed: UpdateQueueSettings) => {
    update.mutate(parsed, {
      onSuccess: () => setSaved(true),
      onError: (error) => {
        for (const field of invalidFields(error.details)) {
          if (FIELD_NAMES.has(field.path)) {
            setError(field.path as keyof UpdateQueueSettingsInput, { type: 'server', message: field.message });
          }
        }
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(save)} noValidate aria-labelledby="queue-settings-title">
      <Stack spacing={4}>
        <SenderSection control={control} mailDomain={settings.mailDomain} replyToPattern={settings.replyToPattern} />
        <EmailTemplatesSection control={control} />
        <QueueRulesSection control={control} />

        {update.isError && (
          <Alert severity="error" onClose={() => update.reset()}>
            Salvataggio non riuscito: {update.error.message}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
          <Button type="submit" variant="contained" disabled={!formState.isDirty || update.isPending}>
            Salva
          </Button>
          <Button
            type="button"
            onClick={() => {
              reset(values);
              update.reset();
            }}
            disabled={!formState.isDirty || update.isPending}
          >
            Annulla modifiche
          </Button>
          <Typography variant="body2" color="text.secondary">
            Le soglie di servizio (SLA) non sono concordate: quando esisteranno, si imposteranno qui.
          </Typography>
        </Stack>
      </Stack>

      <Snackbar
        open={saved}
        autoHideDuration={6000}
        onClose={() => setSaved(false)}
        message="Impostazioni salvate: valgono per le segnalazioni e le email successive."
      />
    </form>
  );
}
